import {
  FiHome,
  FiBookOpen,
  FiFileText,
  FiAward,
  FiCalendar,
  FiClock,
  FiUsers,
  FiSettings,
  FiBarChart,
  FiCheckSquare,
  FiDollarSign,
  FiTag,
  FiPackage,
  FiShoppingBag,
  FiCreditCard,
  FiEdit3,
  FiBook,
  FiHeart,
  FiTrash2,
  FiDownload,
  FiRefreshCw,
  FiHelpCircle,
  FiLifeBuoy
} from 'react-icons/fi';

/* Student-only navigation structure — grouped into purposeful sections so the
   sidebar reads more like a campus dashboard than a long flat list. The role
   check in the render still gates individual items, but everything below is
   already student-relevant so each item is implicitly studentOnly. */
export const studentNavigationItems = [
  {
    section: 'Home',
    // Expanded by default — this is the landing page right after login.
    defaultExpanded: true,
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: FiHome },
    ],
  },
  {
    section: 'Academics',
    items: [
      { path: '/courses',          label: 'My Courses',           icon: FiBookOpen },
      { path: '/assignments',      label: 'My Assignments',       icon: FiFileText },
      { path: '/student/quizzes',  label: 'My Quizzes & Exams',   icon: FiEdit3 },
      { path: '/extensions/new',   label: 'My Extension Requests', icon: FiClock },
      { path: '/electives',        label: 'My Electives',         icon: FiBookOpen },
      { path: '/catalog',          label: 'Course Registration',  icon: FiBookOpen },
    ],
  },
  {
    section: 'Progress & Performance',
    items: [
      { path: '/grades',     label: 'My Grades',     icon: FiAward },
      { path: '/attendance', label: 'My Attendance', icon: FiCheckSquare },
    ],
  },
  {
    section: 'Learning Resources',
    items: [
      { path: '/student/notes',      label: 'My Notes',  icon: FiBook },
      { path: '/student/group-poll', label: 'Group Poll', icon: FiBarChart },
    ],
  },
  {
    section: 'Schedule',
    items: [
      { path: '/calendar', label: 'Calendar', icon: FiCalendar },
    ],
  },
  {
    section: 'Billing & Support',
    items: [
      { path: '/support/report',      label: 'Report an Issue',    icon: FiHelpCircle },
      { path: '/support/my-tickets',  label: 'My Support Tickets', icon: FiLifeBuoy },
      { path: '/payment-history',     label: 'Payment History',    icon: FiCreditCard },
      { path: '/donation',            label: 'Make a Donation',    icon: FiHeart },
    ],
  },
  {
    section: 'Account',
    items: [
      { path: '/settings', label: 'Settings', icon: FiSettings },
    ],
  },
];

/* Admin-only navigation structure — a dedicated array (not a filtered view of
   `navigationItems`) so admin's grouping can differ from what instructors see
   without leaving instructors with half-empty sections (e.g. "Billing &
   Pricing" would render with zero items for an instructor, since every item
   in it is adminOnly). Every item here is plain admin-visible content, so
   none of them need role flags — admin already satisfies every existing flag
   combination that would otherwise apply. */
export const adminNavigationSections = [
  {
    section: 'Main',
    // Expanded by default — this is the landing page right after login.
    defaultExpanded: true,
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: FiHome },
      { path: '/courses', label: 'Courses', icon: FiBookOpen },
      { path: '/assignments', label: 'Assignments', icon: FiFileText },
      { path: '/instructor/quizzes', label: 'Quizzes', icon: FiEdit3 },
      { path: '/instructor/gradebook', label: 'Gradebook', icon: FiAward },
      { path: '/calendar', label: 'Calendar', icon: FiCalendar },
      { path: '/donation', label: 'Make a Donation', icon: FiHeart },
      { path: '/settings', label: 'Settings', icon: FiSettings },
    ],
  },
  {
    section: 'Community',
    items: [
      { path: '/students', label: 'Students', icon: FiUsers },
    ],
  },
  {
    section: 'Users & Support',
    items: [
      { path: '/admin/users', label: 'User Management', icon: FiUsers },
      { path: '/admin/support-tickets', label: 'Support Tickets', icon: FiLifeBuoy },
    ],
  },
  {
    section: 'Academics',
    items: [
      { path: '/admin/courses', label: 'Course Management', icon: FiBookOpen },
      { path: '/admin/electives', label: 'Elective Management', icon: FiBookOpen },
      { path: '/admin/attendance', label: 'Attendance Management', icon: FiCheckSquare },
      { path: '/admin/extensions', label: 'Extension Requests', icon: FiCalendar },
    ],
  },
  {
    section: 'Billing & Pricing',
    items: [
      { path: '/admin/pricing', label: 'Course Pricing', icon: FiDollarSign },
      { path: '/admin/promo-codes', label: 'Promo Codes', icon: FiTag },
      { path: '/admin/bulk-pricing', label: 'Bulk Pricing', icon: FiPackage },
      { path: '/admin/additional-products', label: 'Additional Products', icon: FiShoppingBag },
      { path: '/admin/purchase-lookup', label: 'Purchase Lookup', icon: FiCreditCard },
    ],
  },
  {
    section: 'Engagement',
    items: [
      { path: '/instructor/polls', label: 'Poll Manager', icon: FiBarChart },
      { path: '/instructor/live-poll', label: 'Live Poll Board', icon: FiBarChart },
    ],
  },
  {
    section: 'Reports',
    items: [
      { path: '/admin/reports', label: 'Enrollment Reports', icon: FiBarChart },
      { path: '/admin/reports/student-grades', label: 'Student Grades Report', icon: FiCheckSquare },
    ],
  },
  {
    section: 'System',
    items: [
      { path: '/admin/import-export', label: 'Import/Export', icon: FiDownload },
      { path: '/admin/deleted-items', label: 'Recently Deleted', icon: FiTrash2 },
      { path: '/admin/tools/hls-backfill', label: 'Video HLS Backfill', icon: FiRefreshCw },
    ],
  },
];

/* Instructor-only navigation structure — a dedicated array (same reasoning as
   adminNavigationSections above): instructor is now the only role that ever
   reaches this array, so items don't need role flags (they'd all pass the
   "no restrictions" case anyway). "Support Tickets" is relabeled "Student
   Questions" here specifically — an instructor's queue only ever contains
   Course Question tickets from their own students, so that's a more accurate
   name than the generic admin-facing "Support Tickets". */
export const instructorNavigationSections = [
  {
    section: 'Home',
    // Expanded by default — this is the landing page right after login.
    defaultExpanded: true,
    items: [
      { path: '/dashboard', label: 'Dashboard', icon: FiHome },
    ],
  },
  {
    section: 'Academics',
    items: [
      { path: '/courses', label: 'Courses', icon: FiBookOpen },
      { path: '/assignments', label: 'Assignments', icon: FiFileText },
      { path: '/instructor/quizzes', label: 'Quizzes & Exams', icon: FiEdit3 },
      { path: '/admin/extensions', label: 'Extension Requests', icon: FiCalendar },
      { path: '/admin/support-tickets', label: 'Student Questions', icon: FiLifeBuoy },
      { path: '/admin/courses', label: 'Course Management', icon: FiBookOpen },
      { path: '/admin/electives', label: 'Elective Management', icon: FiBookOpen },
    ],
  },
  {
    section: 'Community',
    items: [
      { path: '/students', label: 'Students', icon: FiUsers },
    ],
  },
  {
    section: 'Progress & Performance',
    items: [
      { path: '/instructor/gradebook', label: 'Gradebook', icon: FiAward },
      { path: '/admin/attendance', label: 'Attendance Management', icon: FiCheckSquare },
      { path: '/admin/reports/student-grades', label: 'Student Grades Report', icon: FiCheckSquare },
    ],
  },
  {
    section: 'Learning Resources',
    items: [
      { path: '/instructor/polls', label: 'Poll Manager', icon: FiBarChart },
      { path: '/instructor/live-poll', label: 'Live Poll Board', icon: FiBarChart },
    ],
  },
  {
    section: 'Schedule',
    items: [
      { path: '/calendar', label: 'Calendar', icon: FiCalendar },
    ],
  },
  {
    section: 'Billing & Support',
    items: [
      { path: '/support/report', label: 'Report an Issue', icon: FiHelpCircle },
      { path: '/support/my-tickets', label: 'My Support Tickets', icon: FiLifeBuoy },
      { path: '/donation', label: 'Make a Donation', icon: FiHeart },
    ],
  },
  {
    section: 'Account',
    items: [
      { path: '/settings', label: 'Settings', icon: FiSettings },
    ],
  },
];
